import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router'
import compose from 'lodash/fp/compose'

import urlHelper, { removeTrailingSlash } from 'shared/tools/url-helper'
import withAuth, { AuthProps } from 'client/shared/decorators/with-auth'
import * as authActions from 'client/shared/reducers/auth-reducer'
import Auth from 'client/shared/blocks/auth'

import { CurrentUserData } from 'client/shared/types/current-user'
import { Router } from 'client/shared/types/react-router'
import { State as ConnectedState } from 'shared/types/redux'
import { MTNState } from 'client/bookmate/reducers/mtn-reducer'
import { requestPhoneRegistrationCode } from '../reducers/current-user-reducer'
import { buildFullPhoneNumber } from '../blocks/phone-input/phone-input-helpers'
import { isYettelAvailable } from '../helpers/socials-helpers'
import {
  DEFAULT_RESEND_COOLDOWN_SECONDS,
  YETTEL_SERBIA_RESEND_COOLDOWN_SECONDS,
} from '../constants/time-constants'

type Props = {
  data: CurrentUserData
  giftCode: string
  location: Location
  mtn: MTNState
  yettel?: { authInit: boolean }
} & AuthProps &
  Router

type State = {
  timerRunning: boolean
  cooldownSeconds?: number
}

class AuthBox extends Component<Props, State> {
  state = {
    timerRunning: true,
    cooldownSeconds: undefined,
  }
  componentWillUnmount() {
    this.props.dispatch(authActions.clearAuthForm())
  }

  render() {
    const {
      auth: { currentStep },
      location,
      giftCode,
      mtn,
      yettel,
    } = this.props

    if (!currentStep) return null

    return (
      <Auth
        {...this.props}
        yettel={yettel?.authInit}
        mtn={mtn}
        timerRunning={this.state.timerRunning}
        stopTimer={this.stopTimer}
        cooldownSeconds={this.state.cooldownSeconds}
        resendCode={this.resendCode}
        changeStep={this.changeStep}
        currentStep={currentStep}
        {...(location.pathname ===
        removeTrailingSlash(urlHelper.codePage(undefined, location.query))
          ? { specialParams: { giftCode } }
          : {})}
      />
    )
  }

  resendCode = async () => {
    const { dispatch, auth, mtn, data, yettel } = this.props

    const isYettelSerbia =
      Boolean(yettel?.authInit) && isYettelAvailable(data.country, data.locale)

    this.setState({ timerRunning: true, cooldownSeconds: undefined })

    const extractCooldownSecondsFromMessage = (message: string) => {
      const normalized = message.toLowerCase()

      const looksLikeResendCooldown = /(resend|again|try again|request again)/i.test(
        message,
      )
      if (!looksLikeResendCooldown) return null

      const afterMinutes = normalized.match(
        /after\s+(\d+)\s*(?:minutes?|mins?)\b/i,
      )
      if (afterMinutes?.[1]) return Number(afterMinutes[1]) * 60

      const inMinutes = normalized.match(/in\s+(\d+)\s*(?:minutes?|mins?)\b/i)
      if (inMinutes?.[1]) return Number(inMinutes[1]) * 60

      const afterSeconds = normalized.match(
        /after\s+(\d+)\s*(?:seconds?|secs?)\b/i,
      )
      if (afterSeconds?.[1]) return Number(afterSeconds[1])

      const inSeconds = normalized.match(/in\s+(\d+)\s*(?:seconds?|secs?)\b/i)
      if (inSeconds?.[1]) return Number(inSeconds[1])

      const mmss = normalized.match(/\b(\d{1,2}):(\d{2})\b/)
      if (mmss?.[1] && mmss?.[2]) {
        const minutes = Number(mmss[1])
        const seconds = Number(mmss[2])
        return minutes * 60 + seconds
      }

      return null
    }

    const resolveCooldownSeconds = (parsedSeconds: number | null) => {
      if (isYettelSerbia) {
        if (parsedSeconds) {
          return Math.min(parsedSeconds, YETTEL_SERBIA_RESEND_COOLDOWN_SECONDS)
        }
        return undefined
      }

      return parsedSeconds ?? DEFAULT_RESEND_COOLDOWN_SECONDS
    }

    try {
      if (mtn?.authInit) await dispatch(authActions.sendCode(auth.phone))
      else
        await dispatch(
          requestPhoneRegistrationCode({
            phone_number: buildFullPhoneNumber(auth.countryCode, auth.phone),
          }),
        )

      this.setState({
        cooldownSeconds: resolveCooldownSeconds(null),
        timerRunning: true,
      })
    } catch (e: unknown) {
      const message =
        (typeof e === 'object' &&
          e &&
          'error' in e &&
          (e as any).error?.message) ||
        (typeof e === 'object' && e && 'message' in e && (e as any).message) ||
        ''

      const parsedSeconds =
        typeof message === 'string'
          ? extractCooldownSecondsFromMessage(message)
          : null

      this.setState({
        cooldownSeconds: resolveCooldownSeconds(parsedSeconds),
        timerRunning: true,
      })
    }
  }

  stopTimer = (): void =>
    this.setState({ timerRunning: false, cooldownSeconds: undefined })

  changeStep = (step, sendAnalytics) => {
    this.props.dispatch(authActions.changeStep(step, sendAnalytics))
  }
}

const connectWrapper = connect((state: ConnectedState) => ({
  data: state.currentUser.data,
  giftCode: state.code.codeValue,
  mtn: state.mtn,
  yettel: state.auth.yettel,
}))

const wrappers = compose(
  connectWrapper,
  withAuth({ initialStep: 'social' }),
  withRouter,
)

export default wrappers(AuthBox)
